'use client'

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import {
  getNavbarCollapsedFromCookieClient,
  setClientCookie,
} from '@/utils/cookies/clientCookies'

interface NavbarContextType {
  navbarCollapsed: boolean
  setNavbarCollapsed: (collapsed: boolean) => void
  toggleNavbarCollapsed: () => void
}

const NavbarContext = createContext<NavbarContextType | undefined>(undefined)

export function NavbarProvider({
  children,
  initialCollapsed,
}: {
  children: ReactNode
  initialCollapsed?: boolean
}) {
  const [navbarCollapsed, setNavbarCollapsedState] = useState<boolean>(() => {
    if (typeof initialCollapsed === 'boolean') return initialCollapsed
    return getNavbarCollapsedFromCookieClient()
  })

  useEffect(() => {
    setClientCookie('navbar_collapsed', navbarCollapsed ? '1' : '0')
  }, [navbarCollapsed])

  const setNavbarCollapsed = (collapsed: boolean) => {
    setNavbarCollapsedState(collapsed)
  }

  const toggleNavbarCollapsed = () => {
    setNavbarCollapsedState((prev) => !prev)
  }

  return (
    <NavbarContext.Provider
      value={{ navbarCollapsed, setNavbarCollapsed, toggleNavbarCollapsed }}
    >
      {children}
    </NavbarContext.Provider>
  )
}

export function useNavbarContext() {
  const context = useContext(NavbarContext)
  if (context === undefined) {
    throw new Error('useNavbarContext must be used within a NavbarProvider')
  }
  return context
}
